/**
 * Lookbook Parallax Scroll
 * 3-column layout with scroll-driven parallax animations
 * Uses window scroll to calculate progress through the section
 */

class LookbookParallax {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('[data-lookbook-container]');
    this.firstColumnItems = section.querySelectorAll('[data-parallax="first"]');
    this.secondColumnItems = section.querySelectorAll('[data-parallax="second"]');
    this.thirdColumnItems = section.querySelectorAll('[data-parallax="third"]');
    
    // Get settings from data attributes (with defaults matching React component)
    this.parallaxIntensity = parseInt(section.dataset.parallaxIntensity, 10) || 200;
    this.rotationIntensity = parseInt(section.dataset.rotationIntensity, 10) || 20;
    
    // Current and target values for smooth interpolation
    this.scrollProgress = 0;
    this.currentProgress = 0;
    this.rafId = null;
    this.isInitialized = false;
    
    // Smoothing factor (0-1, lower = smoother but slower)
    this.smoothing = 0.08;
    
    this.init();
  }

  init() {
    if (!this.section) {
      console.warn('Lookbook: No section found');
      return;
    }

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Calculate initial position
    this.calculateScrollProgress();
    this.currentProgress = this.scrollProgress; // Start at current position (no animation on load)
    
    this.bindEvents();
    this.isInitialized = true;
  }

  bindEvents() {
    // Listen to window scroll
    this.scrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    
    // Handle resize
    this.resizeHandler = this.onResize.bind(this);
    window.addEventListener('resize', this.resizeHandler, { passive: true });
    
    // Start the animation loop for smooth interpolation
    this.startAnimationLoop();
  }

  onScroll() {
    // Just update the target progress, the animation loop handles the rest
    this.calculateScrollProgress();
  }

  onResize() {
    this.calculateScrollProgress();
  }
  
  /**
   * Linear interpolation for smooth transitions
   */
  lerp(start, end, factor) {
    return start + (end - start) * factor;
  }
  
  /**
   * Continuous animation loop for smooth parallax
   */
  startAnimationLoop() {
    const animate = () => {
      // Smoothly interpolate current progress towards target
      this.currentProgress = this.lerp(this.currentProgress, this.scrollProgress, this.smoothing);
      
      // Only update DOM if there's meaningful change (performance optimization)
      const diff = Math.abs(this.currentProgress - this.scrollProgress);
      if (diff > 0.0001) {
        this.updateTransforms();
      }
      
      this.rafId = requestAnimationFrame(animate);
    };
    
    this.rafId = requestAnimationFrame(animate);
  }

  calculateScrollProgress() {
    const rect = this.section.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const sectionHeight = this.section.offsetHeight;
    
    // Calculate progress:
    // 0 = section just entered viewport (top of section at bottom of screen)
    // 1 = section just left viewport (bottom of section at top of screen)
    
    // Start: when top of section hits bottom of viewport
    const start = windowHeight;
    // End: when bottom of section hits top of viewport
    const end = -sectionHeight;
    
    // Current position of section top relative to viewport
    const current = rect.top;
    
    // Calculate progress (0 to 1)
    const totalDistance = start - end;
    const traveled = start - current;
    
    this.scrollProgress = Math.max(0, Math.min(1, traveled / totalDistance));
  }

  /**
   * Transform helper - maps scroll progress to value range
   * Similar to Framer Motion's useTransform
   */
  transform(progress, inputRange, outputRange) {
    const [inputStart, inputEnd] = inputRange;
    const [outputStart, outputEnd] = outputRange;
    
    // Clamp progress to input range
    const clampedProgress = Math.max(inputStart, Math.min(inputEnd, progress));
    
    // Map to output range
    const ratio = (clampedProgress - inputStart) / (inputEnd - inputStart);
    return outputStart + ratio * (outputEnd - outputStart);
  }

  updateTransforms() {
    const progress = this.currentProgress;
    
    // First column: moves up-left and rotates left
    // translateY: 0 → -intensity, translateX: 0 → -intensity, rotateZ: 0 → -rotation
    const translateYFirst = this.transform(progress, [0, 1], [0, -this.parallaxIntensity]);
    const translateXFirst = this.transform(progress, [0, 1], [0, -this.parallaxIntensity * 0.5]);
    const rotateFirst = this.transform(progress, [0, 1], [0, -this.rotationIntensity]);
    
    this.firstColumnItems.forEach(item => {
      item.style.transform = `
        translateY(${translateYFirst}px) 
        translateX(${translateXFirst}px) 
        rotateZ(${rotateFirst}deg)
      `;
    });

    // Second column: no movement (static) - just stays in place
    this.secondColumnItems.forEach(item => {
      item.style.transform = 'translateY(0) translateX(0) rotateZ(0deg)';
    });

    // Third column: moves up-right and rotates right
    // translateY: 0 → -intensity, translateX: 0 → +intensity, rotateZ: 0 → +rotation
    const translateYThird = this.transform(progress, [0, 1], [0, -this.parallaxIntensity]);
    const translateXThird = this.transform(progress, [0, 1], [0, this.parallaxIntensity * 0.5]);
    const rotateThird = this.transform(progress, [0, 1], [0, this.rotationIntensity]);
    
    this.thirdColumnItems.forEach(item => {
      item.style.transform = `
        translateY(${translateYThird}px) 
        translateX(${translateXThird}px) 
        rotateZ(${rotateThird}deg)
      `;
    });
  }

  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);
    
    // Reset transforms
    [...this.firstColumnItems, ...this.secondColumnItems, ...this.thirdColumnItems].forEach(item => {
      item.style.transform = '';
    });
  }
}

// Store instances for cleanup
const lookbookInstances = new Map();

// Initialize all lookbook sections
function initLookbooks() {
  const sections = document.querySelectorAll('.lookbook-section');
  
  sections.forEach(section => {
    const sectionId = section.id;
    
    // Skip if already initialized
    if (lookbookInstances.has(sectionId)) return;
    
    const instance = new LookbookParallax(section);
    lookbookInstances.set(sectionId, instance);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLookbooks);
} else {
  initLookbooks();
}

// Handle Shopify theme editor events
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.lookbook-section');
  if (section) {
    const sectionId = section.id;
    
    // Destroy existing instance if any
    if (lookbookInstances.has(sectionId)) {
      lookbookInstances.get(sectionId).destroy();
      lookbookInstances.delete(sectionId);
    }
    
    const instance = new LookbookParallax(section);
    lookbookInstances.set(sectionId, instance);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('.lookbook-section');
  if (section) {
    const sectionId = section.id;
    
    if (lookbookInstances.has(sectionId)) {
      lookbookInstances.get(sectionId).destroy();
      lookbookInstances.delete(sectionId);
    }
  }
});

// Handle block selection in theme editor
document.addEventListener('shopify:block:select', (event) => {
  const section = event.target.closest('.lookbook-section');
  if (section && event.target) {
    // Scroll to the selected block
    event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
});
